import {effect, inject, Injectable, Injector, runInInjectionContext, signal, WritableSignal} from '@angular/core';
import {PlatformService} from "./platform.service";
import {StorageKey, StorageService, TypeFromTypia, keyTypeMapping} from "./storage.service";

@Injectable({
  providedIn: 'root'
})
export class BrowserStorageService extends StorageService {
  private readonly injector = inject(Injector);
  private readonly platformService = inject(PlatformService);

  private async getPreferencesModule() {
    if (!this.platformService.isBrowser) return null;

    const { Preferences } = await import('@capacitor/preferences');
    return {
      get: Preferences.get.bind(Preferences),
      set: Preferences.set.bind(Preferences),
      remove: Preferences.remove.bind(Preferences),
      clear: Preferences.clear.bind(Preferences),
      keys: Preferences.keys.bind(Preferences),
    };
  }

  async setItem<T extends StorageKey>(key: T, value: TypeFromTypia<typeof keyTypeMapping[T]>) {
    const preferences = await this.getPreferencesModule();
    if (!preferences) {
      return;
    }

    await preferences.set({
      key,
      value: JSON.stringify(value),
    });
  }

  async updateItem<T extends StorageKey>(key: T, updateFn: (value: TypeFromTypia<typeof keyTypeMapping[T]> | undefined) => TypeFromTypia<typeof keyTypeMapping[T]>) {
    const item = await this.getItem(key);

    await this.setItem(key, updateFn(item));
  }

  async getItem<T extends StorageKey>(key: T): Promise<TypeFromTypia<typeof keyTypeMapping[T]> | undefined> {
    const preferences = await this.getPreferencesModule();
    if (!preferences) {
      return undefined;
    }

    const item = await preferences.get({key});

    if (item && item.value) {
      const parsed = JSON.parse(item.value);
      if (keyTypeMapping[key](parsed)) {
        return parsed as TypeFromTypia<typeof keyTypeMapping[T]>;
      } else {
        console.error(`Error parsing item from sessionStorage with key: ${key}`);
        await preferences.remove({key});
      }
    }

    return undefined;
  }

  generateItemSignal<T extends StorageKey>(key: T, initialValue: TypeFromTypia<typeof keyTypeMapping[T]>): WritableSignal<TypeFromTypia<typeof keyTypeMapping[T]>> {
    return runInInjectionContext(this.injector, () => {
      const val = signal(initialValue);

      this.getItem(key).then(
        (value) => {
          if (value) {
            val.set(value);
          }
        }
      )

      effect(() => {
        const value = val();

        if (value !== initialValue) {
          this.setItem(key, value);
        }
      });

      return val;
    })
  }

  async removeItem<T extends StorageKey>(key: T) {
    const preferences = await this.getPreferencesModule();
    if (!preferences) {
      return;
    }

    await preferences.remove({key});
  }

  async clear() {
    const preferences = await this.getPreferencesModule();
    if (!preferences) {
      return;
    }

    await preferences.clear();
  }
}
